import React, { useCallback, useEffect } from "react";

function useAsyncSearch<T>(
  query: string,
  searchFn?: (query: string, callback: (options?: any) => void) => any,
  initialItems: any[] = []
): [boolean, Array<T>] {
  const [items, setItems] = React.useState<any[]>(initialItems);
  const [isLoading, setIsLoading] = React.useState(false);
  const isMounted = React.useRef(true);

  const loadOptions = useCallback(
    (inputValue: string, callback: (options?: any) => void) => {
      if (!inputValue) return;

      if (!searchFn) return callback();

      const loader = searchFn(inputValue, callback);

      if (loader && typeof loader.then === "function") {
        loader.then(callback, () => callback());
      }
    },
    [searchFn]
  );

  const filterItems = (query: string) => {
    if (query === "") return initialItems;

    return initialItems.filter((item: any) => {
      return item.label.toLowerCase().indexOf(query.toLowerCase()) > -1;
    });
  };

  useEffect(() => {
    if (!query) {
      setItems(initialItems);
      return;
    }

    if (searchFn !== undefined) {
      setIsLoading(true);
      loadOptions(query, (options) => {
        if (isMounted.current) {
          setItems(options);
          setIsLoading(false);
        }
      });
    } else {
      const filteredItems = filterItems(query);
      setItems(filteredItems);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, initialItems, searchFn, loadOptions]);

  return [isLoading, items];
}

export default useAsyncSearch;
